import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import { describePasskeyError, reportPasskeyError } from "../lib/passkeyDebug";
import { BUILD_ID } from "../buildInfo";
import { Capacitor } from "@capacitor/core";
import { api, BACKEND_URL, getBioToken, setBioToken } from "../api";
import { PhoneInput } from "../components/PhoneInput";
import { biometricAvailable, biometricAuthenticate } from "../lib/biometric";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { toast } from "sonner";
import { Globe, Fingerprint, LogIn, UserPlus, Loader2, Eye, EyeOff, KeyRound, ArrowLeft, MailCheck, ShieldCheck } from "lucide-react";

/** Official multicolor Google "G" mark (lucide has no Google icon). */
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

/** Official Apple logo mark (Apple Design Resources shape) — required by
 *  App Store Guideline 4 for the Sign in with Apple button. */
const AppleLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 814 1000" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"
    />
  </svg>
);

export default function LoginPage() {
  const { login, lang, setLang, t } = useApp();
  const navigate = useNavigate();

  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // login form
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("pmf_last_email") || ""; } catch (e) { return ""; }
  });
  // Highlight FaceID when this device already used/created a passkey
  const [hasPasskey] = useState(() => {
    try { return localStorage.getItem("pmf_has_passkey") === "1"; } catch (e) { return false; }
  });
  const [password, setPassword] = useState("");

  // register form
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regRole, setRegRole] = useState("installer");
  const [regPending, setRegPending] = useState(false); // request sent, awaiting admin approval
  const [regMode, setRegMode] = useState("solo"); // solo | join_company | create_company
  const [regCompanyName, setRegCompanyName] = useState("");
  const [regCompanyCode, setRegCompanyCode] = useState("");
  const [regPhone, setRegPhone] = useState("");

  // forgot password flow
  const [fpStep, setFpStep] = useState(1); // 1=email, 2=code, 3=new password
  const [fpEmail, setFpEmail] = useState("");
  const [fpCode, setFpCode] = useState("");
  const [fpPw1, setFpPw1] = useState("");
  const [fpPw2, setFpPw2] = useState("");
  const [fpCooldown, setFpCooldown] = useState(0);

  useEffect(() => {
    if (fpCooldown <= 0) return;
    const iv = setInterval(() => setFpCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(iv);
  }, [fpCooldown]);

  const fpErrorMsg = (err) => {
    const detail = err?.response?.data?.detail || "";
    const known = ["invalid_code", "code_expired", "too_many_attempts", "no_reset_request", "email_send_failed"];
    if (known.includes(detail)) {
      if (detail === "too_many_attempts" || detail === "code_expired" || detail === "no_reset_request") {
        // these invalidate the code — send the user back to step 1
        setFpStep(1);
        setFpCode("");
      }
      return t(detail);
    }
    if (detail === "password_too_short") return t("password_min");
    return detail || t("email_send_failed");
  };

  const openForgot = () => {
    setFpStep(1);
    setFpEmail(email.trim());
    setFpCode("");
    setFpPw1("");
    setFpPw2("");
    setTab("forgot");
  };

  const doSendCode = async (e) => {
    e?.preventDefault?.();
    if (!fpEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: fpEmail.trim() });
      setFpCooldown(data?.cooldown || 60);
      toast.success(t("code_sent"));
      setFpStep(2);
      setFpCode("");
    } catch (err) {
      toast.error(fpErrorMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const doVerifyCode = async (e) => {
    e?.preventDefault?.();
    if (fpCode.length !== 6) return;
    setBusy(true);
    try {
      await api.post("/auth/verify-reset-code", { email: fpEmail.trim(), code: fpCode });
      setFpStep(3);
    } catch (err) {
      toast.error(fpErrorMsg(err));
      setFpCode("");
    } finally {
      setBusy(false);
    }
  };

  const doResetPassword = async (e) => {
    e?.preventDefault?.();
    if (fpPw1.length < 6) {
      toast.error(t("password_min"));
      return;
    }
    if (fpPw1 !== fpPw2) {
      toast.error(t("passwords_mismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email: fpEmail.trim(), code: fpCode, new_password: fpPw1 });
      toast.success(t("password_reset_success"));
      setEmail(fpEmail.trim());
      setPassword("");
      setTab("login");
      setFpStep(1);
      setFpCode("");
      setFpPw1("");
      setFpPw2("");
    } catch (err) {
      toast.error(fpErrorMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const finishAuth = (data, msg) => {
    login(data.token, data.user);
    toast.success(msg);
    navigate("/messages");
  };

  // Surface Sign-in-with-Apple / Google errors coming back from the backend redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // QR invite: /?join=CODE opens the register tab with the company code pre-filled
    const joinCode = params.get("join") || sessionStorage.getItem("pending_join");
    if (joinCode) {
      sessionStorage.removeItem("pending_join");
      setTab("register");
      setRegMode("join_company");
      setRegCompanyCode(joinCode.toUpperCase());
      window.history.replaceState({}, "", window.location.pathname);
    }
    const appleError = params.get("apple_error");
    const googleError = params.get("google_error");
    if (appleError) {
      if (appleError !== "cancelled") toast.error(`${t("apple_login_failed")} (${appleError})`);
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (googleError) {
      if (googleError === "account_not_found") {
        toast.error(t("google_account_not_found"));
      } else if (googleError !== "cancelled") {
        toast.error(`${t("google_login_failed")} (${googleError})`);
      }
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Native deep link (?join=) arriving while the login page is already open
  useEffect(() => {
    const onPendingJoin = () => {
      const code = sessionStorage.getItem("pending_join");
      if (!code) return;
      sessionStorage.removeItem("pending_join");
      setTab("register");
      setRegMode("join_company");
      setRegCompanyCode(code.toUpperCase());
    };
    window.addEventListener("pending-join", onPendingJoin);
    return () => window.removeEventListener("pending-join", onPendingJoin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doApple = async () => {
    try {
      const { data } = await api.get("/auth/apple/config");
      if (!data.configured) {
        toast.info(t("apple_soon"));
        return;
      }
      if (Capacitor.isNativePlatform()) {
        // Same pattern as Google: system browser + onsitecomms:// deep link
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url: `${BACKEND_URL}/api/auth/apple/start?native=1` });
        return;
      }
      // Apple's return URL is registered for the PRODUCTION domain only
      const host = window.location.hostname;
      if (!host.endsWith("on-site-comms.com")) {
        toast.info(t("apple_prod_only"));
        return;
      }
      window.location.href = `${BACKEND_URL}/api/auth/apple/start`;
    } catch (e) {
      toast.info(t("apple_soon"));
    }
  };

  const doGoogle = async () => {
    try {
      const { data } = await api.get("/auth/google/config");
      if (!data.configured) {
        toast.info(t("google_soon"));
        return;
      }
      if (Capacitor.isNativePlatform()) {
        // Google blocks OAuth inside WebViews — open the SYSTEM browser
        // (SFSafariViewController / Chrome Custom Tab). The backend returns the
        // session via the onsitecomms:// deep link (handled in App.js).
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url: `${BACKEND_URL}/api/auth/google/start?native=1` });
        return;
      }
      // Google accepts BOTH the production and the preview callback URLs
      window.location.href = `${BACKEND_URL}/api/auth/google/start`;
    } catch (e) {
      toast.info(t("google_soon"));
    }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      const res = await api.post("/auth/login", { email: email.trim(), password });
      finishAuth(res.data, `${t("welcome_back")}, ${res.data.user.name.split(" ")[0]}!`);
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.detail === "pending_approval") {
        toast.info(t("login_pending_approval"));
      } else {
        toast.error(err?.response?.status === 401 ? t("login_failed") : (err?.response?.data?.detail || t("login_failed")));
      }
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (!regName || !regEmail || !regPassword) return;
    if (regPassword.length < 6) {
      toast.error(t("password_min"));
      return;
    }
    if (regMode === "create_company" && regCompanyName.trim().length < 2) {
      toast.error(t("company_name_required"));
      return;
    }
    if (regMode === "join_company" && !regCompanyCode.trim()) {
      toast.error(t("company_code_required"));
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/auth/register", {
        name: regName.trim(),
        email: regEmail.trim(),
        password: regPassword,
        role: regRole,
        language: lang,
        mode: regMode,
        company_name: regCompanyName.trim(),
        company_code: regCompanyCode.trim().toUpperCase(),
        phone: regPhone.trim(),
      });
      if (res.data?.pending) {
        // Approval-gated (join company): show the "request sent" confirmation screen
        setRegPending(true);
        setRegPassword("");
      } else if (res.data?.token) {
        // solo / create_company: account is live immediately
        if (res.data?.company?.join_code) {
          toast.success(`${t("company_created_toast")} — ${res.data.company.name}`, { duration: 8000 });
        }
        finishAuth(res.data, t("account_created"));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail === "registration_pending") toast.info(t("register_already_pending"));
      else if (detail === "invalid_company_code") toast.error(t("invalid_company_code"));
      else if (detail === "company_code_required") toast.error(t("company_code_required"));
      else if (detail === "company_name_required") toast.error(t("company_name_required"));
      else if (detail === "phone_exists") toast.error(t("phone_exists"));
      else if (detail === "email_pending") toast.error(t("email_pending"));
      else if (detail === "invalid_phone") toast.error(t("invalid_phone"));
      else toast.error(err?.response?.status === 409 ? t("email_exists") : (detail || t("register_failed")));
    } finally {
      setBusy(false);
    }
  };

  const doPasskey = async (opts = {}) => {
    const silent = opts?.silent === true;
    setBusy(true);
    try {
      const optRes = await api.post("/auth/webauthn/login-options", { email: email.trim() || null });
      const assertion = await startAuthentication({ optionsJSON: optRes.data });
      const verify = await api.post("/auth/webauthn/login-verify", { credential: assertion });
      try { localStorage.setItem("pmf_has_passkey", "1"); } catch (e) {}
      finishAuth(verify.data, `${t("welcome_back")}, ${verify.data.user.name.split(" ")[0]}!`);
    } catch (err) {
      reportPasskeyError("login-passkey", err);
      if (err?.name === "NotAllowedError") {
        if (!silent) toast.error(t("passkey_cancelled"));
      } else if (!silent) {
        toast.error(`${t("passkey_error")} — ${describePasskeyError(err)}`);
      }
    } finally {
      setBusy(false);
    }
  };

  // NATIVE app + app-lock flow: unlock automatically when the login screen
  // appears (like banking apps). Preferred path: DIRECT Face ID (LAContext via
  // the Biometric plugin — no passkey sheet, no extra tap) unlocking the
  // locally stored session. Fallback: the WebAuthn passkey flow.
  // Skipped after an explicit sign-out and on web browsers.
  const autoPasskeyTried = useRef(false);
  useEffect(() => {
    if (autoPasskeyTried.current) return;
    if (!Capacitor.isNativePlatform()) return;
    try {
      if (sessionStorage.getItem("pmf_skip_auto_passkey") === "1") {
        sessionStorage.removeItem("pmf_skip_auto_passkey");
        autoPasskeyTried.current = true;
        return;
      }
    } catch (e) {}
    autoPasskeyTried.current = true;

    const fallbackToPasskey = () => {
      if (hasPasskey) setTimeout(() => { doPasskey({ silent: true }); }, 400);
    };

    const bioUnlock = async () => {
      const bioToken = getBioToken();
      if (!bioToken) { fallbackToPasskey(); return; }
      try {
        const available = await biometricAvailable();
        if (!available) { fallbackToPasskey(); return; }
        const ok = await biometricAuthenticate(t("bio_unlock_reason"));
        if (!ok) return; // user cancelled — stay on login, no passkey sheet on top
        // Face ID passed — validate the stored session and restore it
        const me = await api.get("/auth/me", { headers: { Authorization: `Bearer ${bioToken}` }, timeout: 8000 });
        finishAuth({ token: bioToken, user: me.data }, `${t("welcome_back")}, ${me.data.name.split(" ")[0]}!`);
      } catch (err) {
        reportPasskeyError("bio-unlock", err);
        if (err?.response?.status === 401) {
          // stored session expired — clean up and fall back to passkey login
          setBioToken(null);
          fallbackToPasskey();
          return;
        }
        // NO INTERNET (or server unreachable): Face ID already passed — open the app with the cached
        // profile in offline mode; the session is re-validated automatically when we are back online
        try {
          const cached = JSON.parse(localStorage.getItem("pmf_user") || "null");
          if (cached?.id) {
            finishAuth({ token: bioToken, user: cached }, t("offline_opened"));
          }
        } catch (e) { /* ignore */ }
      }
    };

    // small delay so the native bridge finishes bootstrapping
    const timer = setTimeout(() => { bioUnlock(); }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roles = [
    { value: "installer", label: t("installer") },
    { value: "supervisor", label: t("supervisor") },
    { value: "office", label: t("office") },
    { value: "manager", label: t("manager") },
  ];

  return (
    <div
      className="min-h-screen bg-background flex flex-col pt-safe"
      style={{
        backgroundImage: "url(/login-bg2.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="w-full max-w-md mx-auto px-4 sm:px-6 py-4 sm:py-10 flex-1 flex flex-col justify-center">
        {/* Brand — centered, prominent */}
        <div className="flex flex-col items-center text-center mt-8 mb-8">
          <img src="/onsitecomms-icon.png" alt="OnSiteComms" className="h-28 w-28 rounded-2xl object-contain drop-shadow-sm" />
          <h1 className="font-display font-bold text-3xl tracking-tight mt-4">
            <span className="text-[#2B3990]">OnSite</span><span className="text-[#C9A03B]">Comms</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("app_tagline")}</p>
        </div>

        <Card className="p-6 border shadow-sm">
          {tab === "login" ? (
            <>
              {hasPasskey && (
                <>
                  <Button
                    type="button"
                    data-testid="login-passkey-primary-btn"
                    onClick={doPasskey}
                    disabled={busy}
                    className="w-full h-12 bg-[#2B3990] hover:bg-[#232e75] text-white"
                  >
                    {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Fingerprint className="h-5 w-5 mr-2" />}
                    {t("sign_in_with_faceid")}
                  </Button>
                  <div className="flex items-center gap-3 my-4">
                    <div className="h-px bg-border flex-1" />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("or")}</span>
                    <div className="h-px bg-border flex-1" />
                  </div>
                </>
              )}
              <form onSubmit={doLogin} className="space-y-3">
                <Input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  autoComplete="email webauthn"
                  placeholder={t("email")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                />
                <div className="relative">
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder={t("password")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button type="submit" data-testid="login-submit-btn" className="w-full h-11" disabled={busy || !email || !password}>
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
                  {t("sign_in")}
                </Button>
                <div className="text-right">
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={openForgot}
                    className="text-xs font-medium text-[#2B3990] hover:underline"
                  >
                    {t("forgot_password")}
                  </button>
                </div>
              </form>

              <div className="flex items-center gap-3 my-4">
                <div className="h-px bg-border flex-1" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("or")}</span>
                <div className="h-px bg-border flex-1" />
              </div>

              <div className="space-y-2">
                {/* Sign in with Apple — official style (black, Apple logo, full width)
                    per Apple Human Interface Guidelines / App Store Guideline 4 */}
                <button
                  type="button"
                  data-testid="apple-login-btn"
                  onClick={doApple}
                  disabled={busy}
                  className="w-full h-11 rounded-lg bg-black text-white text-sm font-medium flex items-center justify-center gap-2.5 transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-50"
                >
                  <AppleLogo className="h-[17px] w-[17px] -mt-0.5" /> {t("signin_with_apple")}
                </button>
                <button
                  type="button"
                  data-testid="google-login-btn"
                  onClick={doGoogle}
                  disabled={busy}
                  className="w-full h-11 rounded-lg bg-white border border-input text-[#1f1f1f] text-sm font-medium flex items-center justify-center gap-2.5 transition-colors hover:bg-muted/50 active:bg-muted disabled:opacity-50"
                >
                  <GoogleIcon className="h-[18px] w-[18px]" /> {t("signin_with_google")}
                </button>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="login-passkey-btn"
                  className="w-full h-11 text-sm font-medium"
                  onClick={doPasskey}
                  disabled={busy}
                >
                  <Fingerprint className="h-[18px] w-[18px] mr-2 text-[#2B3990]" /> {t("signin_with_faceid")}
                </Button>
              </div>

              <p className="text-center text-xs text-muted-foreground mt-5">
                {t("no_account_yet")}{" "}
                <button
                  type="button"
                  data-testid="show-register-link"
                  onClick={() => setTab("register")}
                  className="font-semibold text-[#2B3990] hover:underline"
                >
                  {t("create_account")}
                </button>
              </p>
            </>
          ) : tab === "register" ? (
            regPending ? (
              <div className="flex flex-col items-center text-center py-6" data-testid="register-pending-screen">
                <div className="h-14 w-14 rounded-full bg-[#2B3990]/10 flex items-center justify-center mb-4">
                  <MailCheck className="h-7 w-7 text-[#2B3990]" />
                </div>
                <h2 className="font-semibold text-lg mb-2">{t("register_pending_title")}</h2>
                <p className="text-sm text-muted-foreground mb-6 max-w-xs">{t("register_pending_sub")}</p>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="register-pending-back-btn"
                  className="h-10"
                  onClick={() => { setRegPending(false); setTab("login"); }}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" /> {t("sign_in")}
                </Button>
              </div>
            ) : (
            <>
              <form onSubmit={doRegister} className="space-y-3">
                {/* --- Company mode: solo (default) | join with code | create --- */}
                <div className="grid grid-cols-3 gap-1.5" data-testid="register-mode-toggle">
                  {[
                    { v: "solo", label: t("reg_mode_solo") },
                    { v: "join_company", label: t("reg_mode_join") },
                    { v: "create_company", label: t("reg_mode_create") },
                  ].map((m) => (
                    <button
                      key={m.v}
                      type="button"
                      data-testid={`register-mode-${m.v}`}
                      onClick={() => setRegMode(m.v)}
                      className={`h-9 rounded-lg border text-[11px] font-semibold leading-tight px-1 transition-colors ${
                        regMode === m.v
                          ? "bg-[#2B3990] text-white border-[#2B3990]"
                          : "bg-background text-muted-foreground border-input hover:border-[#2B3990]/50"
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-start gap-2 rounded-lg bg-[#2B3990]/5 border border-[#2B3990]/15 px-3 py-2" data-testid="register-approval-notice">
                  <ShieldCheck className="h-4 w-4 text-[#2B3990] mt-0.5 shrink-0" />
                  <p className="text-[11px] leading-snug text-muted-foreground">
                    {regMode === "solo" ? t("reg_solo_notice") : regMode === "join_company" ? t("register_approval_notice") : t("reg_create_notice")}
                  </p>
                </div>
                {regMode === "create_company" && (
                  <Input
                    id="reg-company-name"
                    data-testid="register-company-name-input"
                    placeholder={t("company_name")}
                    value={regCompanyName}
                    onChange={(e) => setRegCompanyName(e.target.value)}
                    className="h-11"
                  />
                )}
                {regMode === "join_company" && (
                  <Input
                    id="reg-company-code"
                    data-testid="register-company-code-input"
                    placeholder={t("company_code")}
                    value={regCompanyCode}
                    onChange={(e) => setRegCompanyCode(e.target.value.toUpperCase())}
                    className="h-11 font-mono-plex tracking-widest uppercase"
                    maxLength={10}
                  />
                )}
                <Input
                  id="reg-name"
                  data-testid="register-name-input"
                  autoComplete="name"
                  placeholder={t("your_name")}
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  className="h-11"
                />
                <Input
                  id="reg-email"
                  data-testid="register-email-input"
                  type="email"
                  autoComplete="email"
                  placeholder={t("email")}
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  className="h-11"
                />
                <PhoneInput
                  dataTestId="register-phone-input"
                  placeholder={t("phone_optional")}
                  value={regPhone}
                  onChange={setRegPhone}
                />
                <Input
                  id="reg-password"
                  data-testid="register-password-input"
                  type="password"
                  autoComplete="new-password"
                  placeholder={t("password_ph")}
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  className="h-11"
                />
                {regMode !== "create_company" && (
                <Select value={regRole} onValueChange={setRegRole}>
                  <SelectTrigger data-testid="register-role-select" className="h-11">
                    <SelectValue placeholder={t("role")} />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.value} value={r.value} data-testid={`register-role-${r.value}`}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                )}
                <Button type="submit" data-testid="register-submit-btn" className="w-full h-11" disabled={busy || !regName || !regEmail || !regPassword}>
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                  {t("create_account")}
                </Button>
              </form>

              <p className="text-center text-xs text-muted-foreground mt-5">
                {t("already_have_account")}{" "}
                <button
                  type="button"
                  data-testid="show-login-link"
                  onClick={() => setTab("login")}
                  className="font-semibold text-[#2B3990] hover:underline"
                >
                  {t("sign_in")}
                </button>
              </p>
            </>
            )
          ) : (
            <>
              {/* ---- Forgot password flow (3 steps) ---- */}
              {fpStep === 1 && (
                <form onSubmit={doSendCode} className="space-y-3" data-testid="forgot-step-email">
                  <div className="flex flex-col items-center text-center mb-1">
                    <div className="h-10 w-10 rounded-full bg-[#2B3990]/10 flex items-center justify-center mb-2">
                      <KeyRound className="h-5 w-5 text-[#2B3990]" />
                    </div>
                    <h2 className="font-semibold text-base">{t("forgot_password_title")}</h2>
                    <p className="text-xs text-muted-foreground mt-1">{t("forgot_password_sub")}</p>
                  </div>
                  <Input
                    id="fp-email"
                    data-testid="forgot-email-input"
                    type="email"
                    autoComplete="email"
                    placeholder={t("email")}
                    value={fpEmail}
                    onChange={(e) => setFpEmail(e.target.value)}
                    className="h-11"
                  />
                  <Button type="submit" data-testid="forgot-send-code-btn" className="w-full h-11" disabled={busy || !fpEmail.trim()}>
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MailCheck className="h-4 w-4 mr-2" />}
                    {t("send_code")}
                  </Button>
                </form>
              )}

              {fpStep === 2 && (
                <form onSubmit={doVerifyCode} className="space-y-3" data-testid="forgot-step-code">
                  <div className="flex flex-col items-center text-center mb-1">
                    <div className="h-10 w-10 rounded-full bg-[#2B3990]/10 flex items-center justify-center mb-2">
                      <MailCheck className="h-5 w-5 text-[#2B3990]" />
                    </div>
                    <h2 className="font-semibold text-base">{t("enter_code_title")}</h2>
                    <p className="text-xs text-muted-foreground mt-1">{t("enter_code_sub")}</p>
                  </div>
                  <div className="flex justify-center" data-testid="forgot-code-input">
                    <InputOTP maxLength={6} value={fpCode} onChange={setFpCode} inputMode="numeric">
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} className="h-11 w-10" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button type="submit" data-testid="forgot-verify-code-btn" className="w-full h-11" disabled={busy || fpCode.length !== 6}>
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                    {t("verify_code")}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      data-testid="forgot-resend-code-btn"
                      onClick={doSendCode}
                      disabled={busy || fpCooldown > 0}
                      className="text-xs font-medium text-[#2B3990] hover:underline disabled:text-muted-foreground disabled:no-underline"
                    >
                      {fpCooldown > 0 ? `${t("resend_in")} ${fpCooldown}s` : t("resend_code")}
                    </button>
                  </div>
                </form>
              )}

              {fpStep === 3 && (
                <form onSubmit={doResetPassword} className="space-y-3" data-testid="forgot-step-password">
                  <div className="flex flex-col items-center text-center mb-1">
                    <div className="h-10 w-10 rounded-full bg-[#2B3990]/10 flex items-center justify-center mb-2">
                      <ShieldCheck className="h-5 w-5 text-[#2B3990]" />
                    </div>
                    <h2 className="font-semibold text-base">{t("new_password_title")}</h2>
                    <p className="text-xs text-muted-foreground mt-1">{t("new_password_sub")}</p>
                  </div>
                  <Input
                    id="fp-pw1"
                    data-testid="forgot-new-password-input"
                    type="password"
                    autoComplete="new-password"
                    placeholder={t("new_password")}
                    value={fpPw1}
                    onChange={(e) => setFpPw1(e.target.value)}
                    className="h-11"
                  />
                  <Input
                    id="fp-pw2"
                    data-testid="forgot-confirm-password-input"
                    type="password"
                    autoComplete="new-password"
                    placeholder={t("confirm_password")}
                    value={fpPw2}
                    onChange={(e) => setFpPw2(e.target.value)}
                    className="h-11"
                  />
                  <Button type="submit" data-testid="forgot-save-password-btn" className="w-full h-11" disabled={busy || fpPw1.length < 6 || !fpPw2}>
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
                    {t("save_new_password")}
                  </Button>
                </form>
              )}

              <p className="text-center text-xs text-muted-foreground mt-5">
                <button
                  type="button"
                  data-testid="forgot-back-to-login"
                  onClick={() => { setTab("login"); setFpStep(1); setFpCode(""); }}
                  className="inline-flex items-center gap-1 font-semibold text-[#2B3990] hover:underline"
                >
                  <ArrowLeft className="h-3 w-3" /> {t("back_to_login")}
                </button>
              </p>
            </>
          )}
        </Card>

        {/* Language switch — segmented pill below the card */}
        <div className="flex justify-center mt-5">
          <div
            data-testid="language-toggle"
            className="inline-flex items-center gap-0.5 rounded-full border bg-card shadow-sm p-0.5"
          >
            <button
              type="button"
              data-testid="lang-en-btn"
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                lang === "en" ? "bg-[#2B3990] text-white" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              English
            </button>
            <Globe className="h-3 w-3 text-muted-foreground" />
            <button
              type="button"
              data-testid="lang-pt-btn"
              onClick={() => setLang("pt")}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                lang === "pt" ? "bg-[#2B3990] text-white" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Português
            </button>
            <button
              type="button"
              data-testid="lang-es-btn"
              onClick={() => setLang("es")}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                lang === "es" ? "bg-[#2B3990] text-white" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Español
            </button>
          </div>
        </div>
      </div>

      {/* Copyright footer */}
      <footer
        data-testid="login-copyright"
        className="py-5 pb-[max(env(safe-area-inset-bottom,0px),1.25rem)] text-center text-[11px] text-muted-foreground"
      >
        Copyright © 2026 Johnny Garbim® — All rights reserved.
        <span className="block mt-0.5 opacity-60" data-testid="login-build-id">Build {BUILD_ID}</span>
      </footer>
    </div>
  );
}
